export type RecipeCategory =
  | 'beverage'
  | 'fusion'
  | 'streetFood'
  | 'vrat'
  | 'appetizer'
  | 'rice'
  | 'chaat'
  | 'homemade'
  | 'quickSnacks'
  | 'bread'
  | 'curries'
  | 'rotimatic'
  | 'noOnionGarlic'
  | 'breakfast'

export interface Recipe {
  id: number
  name: string
  nameHindi?: string
  description: string
  ingredients: string[]
  cuisine: 'Indian' | 'Fusion' | 'Mexican' | 'Asian' | 'Italian' | 'American'
  cookTime: number
  categories: RecipeCategory[]
  videoId: string
  videoUrl: string
  thumbnail: string
  tags: string[]
  isNoOnionGarlic?: boolean
  isVrat?: boolean
}

const yt = (id: string) => `https://www.youtube.com/watch?v=${id}`
const thumb = (id: string) =>
  `https://img.youtube.com/vi/${id}/maxresdefault.jpg`

export const recipes: Recipe[] = [
  // ── BEVERAGE ──────────────────────────────────────────────────────────────
  {
    id: 1,
    name: 'Cookie & Cream Milkshake in 2 Min',
    description:
      'A quick, indulgent milkshake made with cookies and cream — ready in just 2 minutes!',
    ingredients: ['oreo cookies', 'milk', 'ice cream', 'whipped cream'],
    cuisine: 'American',
    cookTime: 2,
    categories: ['beverage', 'quickSnacks'],
    videoId: '-0gQHU4snhU',
    videoUrl: yt('-0gQHU4snhU'),
    thumbnail: thumb('-0gQHU4snhU'),
    tags: ['milkshake', 'beverage', 'quick', 'dessert'],
  },

  // ── FUSION ────────────────────────────────────────────────────────────────
  {
    id: 2,
    name: 'Pav Bhaji Pizza Buns',
    description:
      "Mumbai's iconic pav bhaji meets Italian pizza in these irresistible fusion buns.",
    ingredients: ['pav buns', 'bhaji', 'cheese', 'butter', 'pizza sauce'],
    cuisine: 'Fusion',
    cookTime: 20,
    categories: ['fusion', 'streetFood', 'appetizer'],
    videoId: '-mRkKn8swdk',
    videoUrl: yt('-mRkKn8swdk'),
    thumbnail: thumb('-mRkKn8swdk'),
    tags: ['fusion', 'pizza', 'pav bhaji', 'street food'],
  },
  {
    id: 3,
    name: 'TikTok Tortilla Wrap Hack — 4 Types',
    description:
      'The viral tortilla wrap hack with 4 delicious vegetarian fillings in one video.',
    ingredients: ['tortilla', 'paneer', 'veggies', 'cheese', 'chutney'],
    cuisine: 'Fusion',
    cookTime: 15,
    categories: ['fusion', 'streetFood', 'bread', 'rotimatic', 'breakfast'],
    videoId: 'zAU90Ap1z04',
    videoUrl: yt('zAU90Ap1z04'),
    thumbnail: thumb('zAU90Ap1z04'),
    tags: ['wrap', 'tortilla', 'tiktok', 'quick'],
  },
  {
    id: 4,
    name: 'Rajma Corn Fried Rice',
    description:
      'A protein-packed fusion fried rice with kidney beans and sweet corn.',
    ingredients: ['rice', 'rajma', 'corn', 'soy sauce', 'vegetables'],
    cuisine: 'Fusion',
    cookTime: 25,
    categories: ['fusion', 'rice'],
    videoId: 'WQTC7hlNjoY',
    videoUrl: yt('WQTC7hlNjoY'),
    thumbnail: thumb('WQTC7hlNjoY'),
    tags: ['fried rice', 'rajma', 'fusion', 'protein'],
  },
  {
    id: 5,
    name: 'Caramel Rice Pudding / Kheer',
    nameHindi: 'केरेमल राइज़ पुडिंग',
    description: 'Classic Indian kheer elevated with a luscious caramel twist.',
    ingredients: ['rice', 'milk', 'sugar', 'caramel', 'cardamom'],
    cuisine: 'Fusion',
    cookTime: 30,
    categories: ['fusion', 'quickSnacks'],
    videoId: 'JXQcFzdmBgo',
    videoUrl: yt('JXQcFzdmBgo'),
    thumbnail: thumb('JXQcFzdmBgo'),
    tags: ['kheer', 'dessert', 'caramel', 'fusion'],
  },
  {
    id: 6,
    name: 'Sooji / Rava Pizza — No Maida, No Yeast, No Oven',
    nameHindi: 'सूजी पिज़्ज़ा',
    description:
      'Healthy semolina-based pizza made on a pan — no maida, no yeast, no oven needed!',
    ingredients: ['sooji', 'curd', 'vegetables', 'cheese', 'pizza sauce'],
    cuisine: 'Fusion',
    cookTime: 20,
    categories: ['fusion', 'breakfast'],
    videoId: 'Y_K5zcYtg_g',
    videoUrl: yt('Y_K5zcYtg_g'),
    thumbnail: thumb('Y_K5zcYtg_g'),
    tags: ['pizza', 'sooji', 'no oven', 'healthy'],
  },
  {
    id: 7,
    name: 'Tandoori Dhokla in a Pan',
    nameHindi: 'तंदूरी ढोकला',
    description:
      'Soft, fluffy dhokla with a smoky tandoori twist — made right on the stovetop.',
    ingredients: ['besan', 'curd', 'eno', 'tandoori masala', 'butter'],
    cuisine: 'Fusion',
    cookTime: 25,
    categories: ['fusion', 'streetFood', 'appetizer', 'quickSnacks'],
    videoId: 'QQUEneifrfE',
    videoUrl: yt('QQUEneifrfE'),
    thumbnail: thumb('QQUEneifrfE'),
    tags: ['dhokla', 'tandoori', 'fusion', 'snack'],
  },
  {
    id: 8,
    name: 'Pizza Panipuri — Cheese Burst',
    nameHindi: 'पिज़्ज़ा पानी पूरी',
    description:
      'Mind-blowing fusion of pizza and panipuri — cheese burst inside every puri!',
    ingredients: [
      'puri shells',
      'pizza sauce',
      'cheese',
      'vegetables',
      'spices',
    ],
    cuisine: 'Fusion',
    cookTime: 20,
    categories: ['fusion', 'streetFood', 'appetizer', 'chaat', 'quickSnacks'],
    videoId: 'XadTbzn_5PE',
    videoUrl: yt('XadTbzn_5PE'),
    thumbnail: thumb('XadTbzn_5PE'),
    tags: ['panipuri', 'pizza', 'fusion', 'cheese'],
  },
  {
    id: 9,
    name: 'Sattu Energy Bites — No Sugar, No Cook',
    description:
      'Protein-rich no-cook energy bites made with sattu — no sugar, no jaggery needed.',
    ingredients: ['sattu', 'dates', 'nuts', 'coconut', 'cardamom'],
    cuisine: 'Indian',
    cookTime: 10,
    categories: ['fusion', 'quickSnacks', 'breakfast'],
    videoId: '9dFXk4T1Quk',
    videoUrl: yt('9dFXk4T1Quk'),
    thumbnail: thumb('9dFXk4T1Quk'),
    tags: ['sattu', 'energy bites', 'no cook', 'protein'],
  },
  {
    id: 10,
    name: 'No-Dough Paneer Samosa with Tandoori Masala',
    description:
      'Crispy instant samosa without any dough — stuffed with spicy tandoori paneer.',
    ingredients: [
      'paneer',
      'bread slices',
      'tandoori masala',
      'spices',
      'cheese',
    ],
    cuisine: 'Fusion',
    cookTime: 20,
    categories: [
      'fusion',
      'streetFood',
      'appetizer',
      'chaat',
      'quickSnacks',
      'noOnionGarlic',
    ],
    videoId: 'g3rggfrdKSo',
    videoUrl: yt('g3rggfrdKSo'),
    thumbnail: thumb('g3rggfrdKSo'),
    tags: ['samosa', 'paneer', 'instant', 'no dough'],
    isNoOnionGarlic: true,
  },
  {
    id: 11,
    name: 'Crispy Khakhra Sandwich',
    description:
      'A quick, crunchy sandwich using khakhra as the base — easy and yummy!',
    ingredients: ['khakhra', 'cheese', 'vegetables', 'chutney', 'spices'],
    cuisine: 'Fusion',
    cookTime: 10,
    categories: ['fusion', 'appetizer', 'quickSnacks', 'breakfast'],
    videoId: '6UGXHAvMkQY',
    videoUrl: yt('6UGXHAvMkQY'),
    thumbnail: thumb('6UGXHAvMkQY'),
    tags: ['khakhra', 'sandwich', 'crispy', 'quick'],
  },

  // ── STREET FOOD ───────────────────────────────────────────────────────────
  {
    id: 12,
    name: 'Pudla Sandwich',
    nameHindi: 'पुडला सेंडव्हिच',
    description:
      'A healthy chickpea flour crepe sandwich packed with veggies and chutney.',
    ingredients: ['besan', 'vegetables', 'green chutney', 'spices'],
    cuisine: 'Indian',
    cookTime: 15,
    categories: [
      'streetFood',
      'appetizer',
      'quickSnacks',
      'noOnionGarlic',
      'breakfast',
    ],
    videoId: 'nKWGVLtX5Oc',
    videoUrl: yt('nKWGVLtX5Oc'),
    thumbnail: thumb('nKWGVLtX5Oc'),
    tags: ['pudla', 'sandwich', 'besan', 'healthy'],
    isNoOnionGarlic: true,
  },
  {
    id: 13,
    name: 'Quick Tofu Nuggets / Popcorn',
    nameHindi: 'टोफू नगेट / पॉपकॉर्न',
    description:
      'Crispy, golden tofu nuggets that are a healthier plant-based snack.',
    ingredients: ['tofu', 'breadcrumbs', 'spices', 'cornflour'],
    cuisine: 'Asian',
    cookTime: 20,
    categories: ['streetFood', 'appetizer', 'quickSnacks', 'noOnionGarlic'],
    videoId: 'MibYWE_60vM',
    videoUrl: yt('MibYWE_60vM'),
    thumbnail: thumb('MibYWE_60vM'),
    tags: ['tofu', 'nuggets', 'crispy', 'snack'],
    isNoOnionGarlic: true,
  },
  {
    id: 14,
    name: 'Avocado Cheese Sandwich in 5 Minutes',
    nameHindi: 'एवकाडो सैंडविच रेसिपी',
    description:
      'Creamy guacamole cheese sandwich ready in just 5 minutes — a perfect quick bite.',
    ingredients: ['avocado', 'bread', 'cheese', 'lime', 'spices'],
    cuisine: 'Fusion',
    cookTime: 5,
    categories: ['streetFood', 'quickSnacks', 'breakfast'],
    videoId: 'e0zYQdFW2lM',
    videoUrl: yt('e0zYQdFW2lM'),
    thumbnail: thumb('e0zYQdFW2lM'),
    tags: ['avocado', 'sandwich', 'quick', 'breakfast'],
  },
  {
    id: 15,
    name: 'Soya Chili / Soya Chunks Manchurian in 10 Min',
    nameHindi: 'सोया चंक्स मनचूरीयन',
    description:
      'Spicy, saucy soya chunks Manchurian ready in just 10 minutes — restaurant style!',
    ingredients: [
      'soya chunks',
      'soy sauce',
      'chili sauce',
      'capsicum',
      'cornflour',
    ],
    cuisine: 'Asian',
    cookTime: 10,
    categories: ['streetFood', 'appetizer', 'quickSnacks'],
    videoId: 'sWdnzSKqd0M',
    videoUrl: yt('sWdnzSKqd0M'),
    thumbnail: thumb('sWdnzSKqd0M'),
    tags: ['soya', 'manchurian', 'chili', 'quick'],
  },
  {
    id: 16,
    name: 'Masala Pav — Mumbai Street Food',
    description:
      'The iconic Mumbai street food — spicy masala stuffed in buttery pav.',
    ingredients: ['pav', 'butter', 'vegetables', 'masala', 'cheese'],
    cuisine: 'Indian',
    cookTime: 15,
    categories: ['streetFood', 'appetizer', 'chaat'],
    videoId: '3nztHmfJCsM',
    videoUrl: yt('3nztHmfJCsM'),
    thumbnail: thumb('3nztHmfJCsM'),
    tags: ['masala pav', 'mumbai', 'street food', 'quick'],
  },
  {
    id: 17,
    name: 'Paneer Moong Dal Crepe (Chilla)',
    description:
      'Protein-rich savory crepe made with paneer and moong dal — a healthy breakfast.',
    ingredients: ['moong dal', 'paneer', 'vegetables', 'spices'],
    cuisine: 'Indian',
    cookTime: 20,
    categories: ['streetFood', 'breakfast'],
    videoId: 'NcackqB0wT8',
    videoUrl: yt('NcackqB0wT8'),
    thumbnail: thumb('NcackqB0wT8'),
    tags: ['chilla', 'moong dal', 'paneer', 'protein'],
  },
  {
    id: 18,
    name: 'Fusion Chaat — Tortilla Papdi Chaat',
    description:
      'Mexican-Indian fusion chaat using leftover chapati or tortilla as papdi.',
    ingredients: ['tortilla', 'chickpeas', 'curd', 'chutneys', 'spices'],
    cuisine: 'Fusion',
    cookTime: 15,
    categories: ['streetFood', 'chaat', 'bread'],
    videoId: 'FKYy1oj0blw',
    videoUrl: yt('FKYy1oj0blw'),
    thumbnail: thumb('FKYy1oj0blw'),
    tags: ['chaat', 'fusion', 'tortilla', 'papdi'],
  },

  // ── VRAT / FAST ───────────────────────────────────────────────────────────
  {
    id: 19,
    name: '3-Ingredient Caramel Makhana',
    nameHindi: 'केरेमल मखाना',
    description:
      'Crunchy caramel-coated fox nuts with just 3 ingredients — perfect for fasting days.',
    ingredients: ['makhana', 'sugar', 'ghee'],
    cuisine: 'Indian',
    cookTime: 10,
    categories: ['vrat', 'quickSnacks', 'noOnionGarlic'],
    videoId: 'NW6vN9N8Dlw',
    videoUrl: yt('NW6vN9N8Dlw'),
    thumbnail: thumb('NW6vN9N8Dlw'),
    tags: ['makhana', 'vrat', 'caramel', '3 ingredients'],
    isVrat: true,
    isNoOnionGarlic: true,
  },
  {
    id: 20,
    name: 'Easy Kaju Katli — Just 3 Ingredients',
    nameHindi: 'काजू कतली',
    description:
      'Melt-in-mouth cashew fudge with just 3 ingredients — festive and fasting-friendly.',
    ingredients: ['cashews', 'sugar', 'water'],
    cuisine: 'Indian',
    cookTime: 20,
    categories: ['vrat', 'quickSnacks'],
    videoId: 'XA5hGPX40BY',
    videoUrl: yt('XA5hGPX40BY'),
    thumbnail: thumb('XA5hGPX40BY'),
    tags: ['kaju katli', 'sweet', 'vrat', 'festive'],
    isVrat: true,
  },

  // ── APPETIZER ─────────────────────────────────────────────────────────────
  {
    id: 21,
    name: 'Veg Manchurian — No Deep Fry',
    nameHindi: 'बिना तले वेज मंचुरीयन',
    description:
      'Healthy veg Manchurian without deep frying — same great taste, less oil!',
    ingredients: [
      'mixed vegetables',
      'cornflour',
      'soy sauce',
      'chili sauce',
      'ginger',
    ],
    cuisine: 'Asian',
    cookTime: 25,
    categories: ['appetizer'],
    videoId: 'eI3nqxMoEko',
    videoUrl: yt('eI3nqxMoEko'),
    thumbnail: thumb('eI3nqxMoEko'),
    tags: ['manchurian', 'no fry', 'healthy', 'appetizer'],
  },
  {
    id: 22,
    name: 'Tofu Scramble (Tofu Bhurji)',
    nameHindi: 'तोफ़ू भुर्जी',
    description:
      'A plant-based twist on classic bhurji — scrambled tofu with Indian spices.',
    ingredients: ['tofu', 'onion', 'tomato', 'turmeric', 'spices'],
    cuisine: 'Indian',
    cookTime: 15,
    categories: ['appetizer', 'breakfast'],
    videoId: '7raAhRQCgo0',
    videoUrl: yt('7raAhRQCgo0'),
    thumbnail: thumb('7raAhRQCgo0'),
    tags: ['tofu', 'bhurji', 'protein', 'breakfast'],
  },
  {
    id: 23,
    name: 'Paneer Tikka — Restaurant Style',
    nameHindi: 'रेस्टोरेंट जैसा पनीर टिक्का',
    description:
      'Smoky, charred paneer tikka with restaurant-style marinade — made at home!',
    ingredients: ['paneer', 'yogurt', 'tandoori masala', 'capsicum', 'onion'],
    cuisine: 'Indian',
    cookTime: 30,
    categories: ['appetizer', 'quickSnacks'],
    videoId: '8GTrp3e-6Eg',
    videoUrl: yt('8GTrp3e-6Eg'),
    thumbnail: thumb('8GTrp3e-6Eg'),
    tags: ['paneer tikka', 'restaurant style', 'appetizer', 'grilled'],
  },
  {
    id: 24,
    name: 'Rajma & Oats Cutlet / Tikki / Kebab',
    description:
      'Protein-packed cutlets made with kidney beans and oats — crispy outside, soft inside.',
    ingredients: ['rajma', 'oats', 'onion', 'spices', 'breadcrumbs'],
    cuisine: 'Indian',
    cookTime: 25,
    categories: ['appetizer', 'quickSnacks'],
    videoId: 'AvH-hUJlOiQ',
    videoUrl: yt('AvH-hUJlOiQ'),
    thumbnail: thumb('AvH-hUJlOiQ'),
    tags: ['cutlet', 'rajma', 'oats', 'protein'],
  },
  {
    id: 25,
    name: 'Stuffed Pepper / Capsicum',
    nameHindi: 'भरवां शिमला मिर्च',
    description:
      'Colorful bell peppers stuffed with spiced rice and veggies — a wholesome meal.',
    ingredients: ['capsicum', 'rice', 'vegetables', 'cheese', 'spices'],
    cuisine: 'Indian',
    cookTime: 30,
    categories: ['appetizer', 'rice'],
    videoId: '84dRrjasQuk',
    videoUrl: yt('84dRrjasQuk'),
    thumbnail: thumb('84dRrjasQuk'),
    tags: ['stuffed pepper', 'capsicum', 'rice', 'healthy'],
  },
  {
    id: 26,
    name: 'Sooji Veg Rolls — 2 Flavors',
    nameHindi: 'सूजी वेज रॉल्स',
    description:
      'Crispy semolina rolls with two delicious vegetarian fillings.',
    ingredients: ['sooji', 'vegetables', 'cheese', 'spices', 'curd'],
    cuisine: 'Indian',
    cookTime: 25,
    categories: ['appetizer', 'quickSnacks'],
    videoId: 'q0Tkldqouj0',
    videoUrl: yt('q0Tkldqouj0'),
    thumbnail: thumb('q0Tkldqouj0'),
    tags: ['sooji', 'rolls', 'crispy', 'snack'],
  },

  // ── RICE ──────────────────────────────────────────────────────────────────
  {
    id: 27,
    name: 'Protein-Rich Veggie Bowl — Mexican Style',
    nameHindi: 'मेक्सिकन स्टाइल वेज बाउल',
    description:
      'A vibrant Mexican-style rice bowl loaded with protein-rich veggies.',
    ingredients: ['rice', 'black beans', 'corn', 'avocado', 'salsa'],
    cuisine: 'Mexican',
    cookTime: 25,
    categories: ['rice'],
    videoId: 'ZBiEmVHnzzw',
    videoUrl: yt('ZBiEmVHnzzw'),
    thumbnail: thumb('ZBiEmVHnzzw'),
    tags: ['rice bowl', 'mexican', 'protein', 'healthy'],
  },
  {
    id: 28,
    name: 'Kashmiri Pulao',
    nameHindi: 'कश्मीरी पुलाव',
    description:
      'Fragrant, mildly sweet Kashmiri pulao with dry fruits and saffron.',
    ingredients: [
      'basmati rice',
      'dry fruits',
      'saffron',
      'milk',
      'whole spices',
    ],
    cuisine: 'Indian',
    cookTime: 35,
    categories: ['rice'],
    videoId: 'FKsX85LjHUk',
    videoUrl: yt('FKsX85LjHUk'),
    thumbnail: thumb('FKsX85LjHUk'),
    tags: ['kashmiri', 'pulao', 'saffron', 'festive'],
  },

  // ── CHAAT ─────────────────────────────────────────────────────────────────
  {
    id: 29,
    name: '5-Minute Spicy Corn Chaat',
    nameHindi: 'चटपटी कॉर्न चाट',
    description:
      'Tangy, spicy corn chaat ready in 5 minutes — a perfect quick snack.',
    ingredients: ['corn', 'onion', 'tomato', 'chaat masala', 'lemon'],
    cuisine: 'Indian',
    cookTime: 5,
    categories: ['chaat', 'quickSnacks'],
    videoId: 'cmCMk2ihzvA',
    videoUrl: yt('cmCMk2ihzvA'),
    thumbnail: thumb('cmCMk2ihzvA'),
    tags: ['corn chaat', 'quick', 'spicy', 'snack'],
  },
  {
    id: 30,
    name: 'Macaroni Chana Chaat',
    nameHindi: 'मैकरोनी चना चाट',
    description:
      'A fun fusion chaat with macaroni and chickpeas — tangy and filling.',
    ingredients: ['macaroni', 'chickpeas', 'chutneys', 'spices', 'onion'],
    cuisine: 'Fusion',
    cookTime: 15,
    categories: ['chaat'],
    videoId: 'RBofWyDHOpg',
    videoUrl: yt('RBofWyDHOpg'),
    thumbnail: thumb('RBofWyDHOpg'),
    tags: ['macaroni', 'chana', 'chaat', 'fusion'],
  },

  // ── HOMEMADE ──────────────────────────────────────────────────────────────
  {
    id: 31,
    name: 'Homemade Paneer — Market Style',
    nameHindi: 'पनीर बनाए घर पर',
    description:
      'Make fresh, soft paneer at home just like the market — simple and pure.',
    ingredients: ['full fat milk', 'lemon juice or vinegar'],
    cuisine: 'Indian',
    cookTime: 20,
    categories: ['homemade'],
    videoId: 'cutKGfwGF9E',
    videoUrl: yt('cutKGfwGF9E'),
    thumbnail: thumb('cutKGfwGF9E'),
    tags: ['paneer', 'homemade', 'basic', 'dairy'],
  },

  // ── QUICK SNACKS & SWEETS ─────────────────────────────────────────────────
  {
    id: 32,
    name: 'Quick Mug Upma in 5 Minutes',
    nameHindi: '५ मिनट मे उपमा',
    description:
      'Instant mug upma cooked in the microwave in just 5 minutes — perfect for busy mornings.',
    ingredients: ['sooji', 'vegetables', 'mustard seeds', 'curry leaves'],
    cuisine: 'Indian',
    cookTime: 5,
    categories: ['quickSnacks', 'noOnionGarlic', 'breakfast'],
    videoId: 'c0Vs-VwpVEY',
    videoUrl: yt('c0Vs-VwpVEY'),
    thumbnail: thumb('c0Vs-VwpVEY'),
    tags: ['upma', 'mug', 'quick', 'breakfast'],
    isNoOnionGarlic: true,
  },
  {
    id: 33,
    name: 'Easy Homemade Crunchy Chana',
    nameHindi: 'घर पर बनाए कुरकुरे चना',
    description:
      'Roasted crunchy chickpeas — a healthy, addictive snack you can make at home.',
    ingredients: ['chickpeas', 'spices', 'oil'],
    cuisine: 'Indian',
    cookTime: 20,
    categories: ['quickSnacks'],
    videoId: 'yXk4sq8pnCY',
    videoUrl: yt('yXk4sq8pnCY'),
    thumbnail: thumb('yXk4sq8pnCY'),
    tags: ['chana', 'crunchy', 'roasted', 'healthy snack'],
  },
  {
    id: 34,
    name: 'Sooji Veggie Bites',
    nameHindi: 'सूजी के स्वादिष्ट वेजी बाइट्स',
    description:
      'Crispy semolina bites loaded with veggies — a perfect tea-time snack.',
    ingredients: ['sooji', 'vegetables', 'curd', 'spices'],
    cuisine: 'Indian',
    cookTime: 20,
    categories: ['quickSnacks', 'breakfast'],
    videoId: 'p3S_XJqQm8g',
    videoUrl: yt('p3S_XJqQm8g'),
    thumbnail: thumb('p3S_XJqQm8g'),
    tags: ['sooji', 'bites', 'crispy', 'snack'],
  },
  {
    id: 35,
    name: 'Pinwheel Chapati Samosa — No Deep Fry',
    nameHindi: 'रोटी समोसा',
    description:
      'Clever pinwheel samosas made from chapati — no deep frying needed!',
    ingredients: ['chapati', 'potato filling', 'spices', 'butter'],
    cuisine: 'Indian',
    cookTime: 20,
    categories: ['quickSnacks', 'rotimatic'],
    videoId: '-J3T6p2Og94',
    videoUrl: yt('-J3T6p2Og94'),
    thumbnail: thumb('-J3T6p2Og94'),
    tags: ['samosa', 'chapati', 'no fry', 'snack'],
  },
  {
    id: 36,
    name: 'Chocolate Walnut Fudge — 3 Ingredients, 2 Minutes',
    description:
      'Decadent chocolate walnut fudge with just 3 ingredients in 2 minutes.',
    ingredients: ['chocolate', 'condensed milk', 'walnuts'],
    cuisine: 'American',
    cookTime: 2,
    categories: ['quickSnacks'],
    videoId: 'v3Ks3Id9c3w',
    videoUrl: yt('v3Ks3Id9c3w'),
    thumbnail: thumb('v3Ks3Id9c3w'),
    tags: ['chocolate', 'fudge', 'dessert', 'quick'],
  },

  // ── CHAPATI / BREAD ───────────────────────────────────────────────────────
  {
    id: 37,
    name: 'Masala Aloo Puri',
    nameHindi: 'मासाला आलू पुरि',
    description:
      'Crispy, spiced potato puris — a classic Indian breakfast treat.',
    ingredients: ['wheat flour', 'potato', 'spices', 'oil'],
    cuisine: 'Indian',
    cookTime: 30,
    categories: ['bread'],
    videoId: '7bFLfLuHACw',
    videoUrl: yt('7bFLfLuHACw'),
    thumbnail: thumb('7bFLfLuHACw'),
    tags: ['puri', 'aloo', 'breakfast', 'classic'],
  },
  {
    id: 38,
    name: 'Oats Vegetable Paratha',
    nameHindi: 'ओट्स वेजिटेबल पराँठे',
    description:
      'Healthy oats and vegetable stuffed paratha — nutritious and filling.',
    ingredients: ['oats', 'wheat flour', 'vegetables', 'spices'],
    cuisine: 'Indian',
    cookTime: 20,
    categories: ['bread', 'noOnionGarlic', 'breakfast'],
    videoId: 'qkLR6qUGEhw',
    videoUrl: yt('qkLR6qUGEhw'),
    thumbnail: thumb('qkLR6qUGEhw'),
    tags: ['paratha', 'oats', 'healthy', 'breakfast'],
    isNoOnionGarlic: true,
  },
  {
    id: 39,
    name: 'Palak Cheese Paratha',
    nameHindi: 'पालक चीज़ पराठा',
    description:
      'Iron-rich spinach paratha with a gooey cheese filling — kids love it!',
    ingredients: ['spinach', 'wheat flour', 'cheese', 'spices'],
    cuisine: 'Indian',
    cookTime: 20,
    categories: ['bread', 'breakfast'],
    videoId: 'Cbfdwt2T5JY',
    videoUrl: yt('Cbfdwt2T5JY'),
    thumbnail: thumb('Cbfdwt2T5JY'),
    tags: ['paratha', 'palak', 'cheese', 'healthy'],
  },

  // ── CURRIES ───────────────────────────────────────────────────────────────
  {
    id: 40,
    name: 'Quick Kadhai Paneer — Dhaba Style in 15 Min',
    nameHindi: 'ढाबा कढ़ाई पनीर',
    description:
      'Smoky, spicy dhaba-style kadhai paneer ready in just 15 minutes.',
    ingredients: ['paneer', 'capsicum', 'onion', 'tomato', 'kadhai masala'],
    cuisine: 'Indian',
    cookTime: 15,
    categories: ['curries'],
    videoId: 'zSwZA5zHlOM',
    videoUrl: yt('zSwZA5zHlOM'),
    thumbnail: thumb('zSwZA5zHlOM'),
    tags: ['kadhai paneer', 'dhaba', 'quick', 'curry'],
  },
  {
    id: 41,
    name: 'Quick Malai Paneer — No Onion, No Garlic',
    nameHindi: 'झटपट मलाई पनीर',
    description:
      'Creamy, rich malai paneer without onion or garlic — quick and divine.',
    ingredients: ['paneer', 'cream', 'tomato', 'cashews', 'spices'],
    cuisine: 'Indian',
    cookTime: 15,
    categories: ['curries', 'noOnionGarlic'],
    videoId: 'jM7t096udPk',
    videoUrl: yt('jM7t096udPk'),
    thumbnail: thumb('jM7t096udPk'),
    tags: ['malai paneer', 'no onion', 'creamy', 'quick'],
    isNoOnionGarlic: true,
  },
  {
    id: 42,
    name: 'Tofu Pepper Stir Fry — Indian Style',
    nameHindi: 'झटपट तोफ़ु पेप्पर',
    description:
      'Quick Indian-style tofu and pepper stir fry — healthy and flavorful.',
    ingredients: ['tofu', 'capsicum', 'black pepper', 'soy sauce', 'spices'],
    cuisine: 'Fusion',
    cookTime: 15,
    categories: ['curries'],
    videoId: 'dNdSMIc2COA',
    videoUrl: yt('dNdSMIc2COA'),
    thumbnail: thumb('dNdSMIc2COA'),
    tags: ['tofu', 'stir fry', 'pepper', 'quick'],
  },
  {
    id: 43,
    name: 'Lahsuni Palak — Spinach Garlic Curry',
    nameHindi: 'लेह्सुनि पालक',
    description: 'Vibrant green spinach curry with the bold punch of garlic.',
    ingredients: ['spinach', 'garlic', 'onion', 'tomato', 'spices'],
    cuisine: 'Indian',
    cookTime: 20,
    categories: ['curries'],
    videoId: '2mN7IEH1yKc',
    videoUrl: yt('2mN7IEH1yKc'),
    thumbnail: thumb('2mN7IEH1yKc'),
    tags: ['palak', 'spinach', 'garlic', 'healthy'],
  },

  // ── ROTIMATIC ─────────────────────────────────────────────────────────────
  {
    id: 44,
    name: 'Veg Paneer Kathi Rolls',
    nameHindi: 'वेज पनीर काठी रोल',
    description:
      'Delicious paneer kathi rolls made with fresh rotimatic rotis.',
    ingredients: ['roti', 'paneer', 'vegetables', 'chutney', 'spices'],
    cuisine: 'Indian',
    cookTime: 20,
    categories: ['rotimatic'],
    videoId: 'KkqkUbu3ChA',
    videoUrl: yt('KkqkUbu3ChA'),
    thumbnail: thumb('KkqkUbu3ChA'),
    tags: ['kathi roll', 'paneer', 'rotimatic', 'wrap'],
  },
  {
    id: 45,
    name: 'Roti Pakora — No Deep Fry',
    nameHindi: 'बिना तले रोटी के पकोड़े',
    description:
      'Crispy roti pakoras without deep frying — quick, healthy and delicious.',
    ingredients: ['leftover roti', 'besan', 'spices', 'vegetables'],
    cuisine: 'Indian',
    cookTime: 15,
    categories: ['rotimatic', 'quickSnacks'],
    videoId: 'dDM6hmiwvTo',
    videoUrl: yt('dDM6hmiwvTo'),
    thumbnail: thumb('dDM6hmiwvTo'),
    tags: ['roti', 'pakora', 'no fry', 'leftover'],
  },
  {
    id: 46,
    name: 'Vegetable Quesadilla',
    nameHindi: 'वेज़िटेबल कसेडिया',
    description:
      'Cheesy vegetable quesadilla made with rotimatic roti — fusion at its best.',
    ingredients: ['roti', 'cheese', 'vegetables', 'salsa', 'sour cream'],
    cuisine: 'Mexican',
    cookTime: 15,
    categories: ['rotimatic'],
    videoId: 'xkUX1iSqV_A',
    videoUrl: yt('xkUX1iSqV_A'),
    thumbnail: thumb('xkUX1iSqV_A'),
    tags: ['quesadilla', 'cheese', 'rotimatic', 'fusion'],
  },
  {
    id: 47,
    name: 'Roti Patra — Healthy Breakfast Snack',
    nameHindi: 'रोटी पात्रा',
    description:
      'A unique Gujarati-inspired roti patra — healthy, tasty and filling.',
    ingredients: ['roti', 'besan', 'tamarind', 'jaggery', 'spices'],
    cuisine: 'Indian',
    cookTime: 20,
    categories: ['rotimatic', 'breakfast'],
    videoId: '1yCeYD8RoxQ',
    videoUrl: yt('1yCeYD8RoxQ'),
    thumbnail: thumb('1yCeYD8RoxQ'),
    tags: ['roti patra', 'gujarati', 'healthy', 'breakfast'],
  },

  // ── NO ONION / GARLIC ─────────────────────────────────────────────────────
  {
    id: 48,
    name: 'Vegetable Oats — No Onion, No Garlic, No Oil',
    nameHindi: 'वेज्टबल ओट्स',
    description:
      'Super healthy oats with vegetables — zero onion, garlic, or oil!',
    ingredients: ['oats', 'vegetables', 'spices', 'water'],
    cuisine: 'Indian',
    cookTime: 10,
    categories: ['noOnionGarlic', 'breakfast'],
    videoId: '_hScCuXskSA',
    videoUrl: yt('_hScCuXskSA'),
    thumbnail: thumb('_hScCuXskSA'),
    tags: ['oats', 'no oil', 'healthy', 'breakfast'],
    isNoOnionGarlic: true,
  },
]

export const categoryMeta: Record<
  RecipeCategory,
  { label: string; emoji: string; description: string }
> = {
  beverage: {
    label: 'Beverages',
    emoji: '🥤',
    description: 'Refreshing drinks & shakes',
  },
  fusion: {
    label: 'Fusion',
    emoji: '🌮',
    description: 'Indian meets global flavors',
  },
  streetFood: {
    label: 'Street Food',
    emoji: '🛺',
    description: 'Chaat, snacks & street bites',
  },
  vrat: {
    label: 'Vrat / Fast',
    emoji: '🙏',
    description: 'Fasting-friendly recipes',
  },
  appetizer: {
    label: 'Appetizers',
    emoji: '🍢',
    description: 'Starters & finger foods',
  },
  rice: {
    label: 'Rice Dishes',
    emoji: '🍚',
    description: 'Pulao, biryani & rice bowls',
  },
  chaat: {
    label: 'Chaat',
    emoji: '🌶️',
    description: 'Tangy, spicy Indian chaats',
  },
  homemade: {
    label: 'Homemade Basics',
    emoji: '🏠',
    description: 'Make it fresh at home',
  },
  quickSnacks: {
    label: 'Quick Snacks & Sweets',
    emoji: '⚡',
    description: 'Ready in under 20 minutes',
  },
  bread: {
    label: 'Chapati / Bread',
    emoji: '🫓',
    description: 'Paratha, puri & flatbreads',
  },
  curries: {
    label: 'Curries',
    emoji: '🍛',
    description: 'Rich gravies & sabzis',
  },
  rotimatic: {
    label: 'Rotimatic',
    emoji: '🤖',
    description: 'Creative rotimatic recipes',
  },
  noOnionGarlic: {
    label: 'No Onion / Garlic',
    emoji: '🧅',
    description: 'Jain & satvik friendly',
  },
  breakfast: {
    label: 'Breakfast / Snacks',
    emoji: '🌅',
    description: 'Quick & healthy morning bites',
  },
}
